import { reactive, toRefs, Ref } from '@nuxtjs/composition-api';
import { Context } from '@nuxt/types';

interface HealthCheckDataItem {
  id: number;
  projectId?: string;
  patientId?: string;
  scheduledAt: string | null;
  consultedAt: string | null;
  reportedAt: string | null;
  reExamination: boolean | null;
  createdAt: string;
  updatedAt: string;
}

interface State {
  healthCheckData: HealthCheckDataItem | null;
  healthCheckCards: any[]; // TODO: TimelineのHealthCheckCardDataタイプを使用
  isLoading: boolean;
  error: Error | null;
  healthCheckDataMap: Map<string, HealthCheckDataItem>;
}

export interface HealthCheckState {
  // State
  healthCheckData: Ref<HealthCheckDataItem | null>;
  healthCheckCards: Ref<any[]>;
  isLoading: Ref<boolean>;
  error: Ref<Error | null>;
  healthCheckDataMap: Ref<Map<string, HealthCheckDataItem>>;

  // Actions
  fetchHealthCheckData: () => Promise<void>;
  fetchHealthCheckCards: () => Promise<void>;
  updateConsultedStatus: (id: number) => Promise<void>;
  updateReportedStatus: (id: number) => Promise<void>;
  updateReExaminationStatus: (id: number, hasReExamination: boolean) => Promise<void>;
  resetHealthCheckState: () => void;
}

export const useHealthCheckState = (context: Context): HealthCheckState => {
  const { $api } = context as any;

  const state = reactive<State>({
    healthCheckData: null,
    healthCheckCards: [],
    isLoading: false,
    error: null,
    healthCheckDataMap: new Map<string, HealthCheckDataItem>(),
  });

  /**
   * 健康診断データを取得
   */
  const fetchHealthCheckData = async (): Promise<void> => {
    state.isLoading = true;
    state.error = null;

    try {
      const response = await $api.getHealthCheckAppointment();
      const healthCheckData = {
        ...response.data,
        scheduledAt: response.data.scheduledAt ? new Date(response.data.scheduledAt).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' }) : null,
        consultedAt: response.data.consultedAt ? new Date(response.data.consultedAt).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' }) : null,
        reportedAt: response.data.reportedAt ? new Date(response.data.reportedAt).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' }) : null,
      };
      state.healthCheckDataMap.set(response.data.id.toString(), healthCheckData);
      state.healthCheckData = healthCheckData;
    } catch (error) {
      state.error = error as Error;
      console.error('健康診断データの取得に失敗しました:', error);
    } finally {
      state.isLoading = false;
    }
  };

  /**
   * 健康診断関連カードを取得
   */
  const fetchHealthCheckCards = async (): Promise<void> => {
    // TODO: カード取得APIの実装待ち
    // 現在はTimeline APIから健康診断カードをフィルタリングする想定
    console.log('fetchHealthCheckCards: Not implemented yet');
  };

  /**
   * 受診確認ステータスを更新
   */
  const updateConsultedStatus = async (id: number): Promise<void> => {
    state.isLoading = true;
    state.error = null;

    try {
      const response = await $api.healthCheck.confirmConsulted(id);
      // 状態を更新
      if (state.healthCheckData && state.healthCheckData.id === id) {
        state.healthCheckData = {
          ...state.healthCheckData,
          consultedAt: response.consultedAt || new Date().toISOString(),
        };
      }

      // 成功メッセージ
      console.log('受診確認が完了しました');
    } catch (error) {
      state.error = error as Error;
      throw error; // コンポーネント側でもハンドリングできるように
    } finally {
      state.isLoading = false;
    }
  };

  /**
   * 結果受取ステータスを更新
   */
  const updateReportedStatus = async (id: number): Promise<void> => {
    state.isLoading = true;
    state.error = null;

    try {
      const response = await $api.healthCheck.confirmReportReceived(id);
      // 状態を更新
      if (state.healthCheckData && state.healthCheckData.id === id) {
        state.healthCheckData = {
          ...state.healthCheckData,
          reportedAt: response.reportedAt || new Date().toISOString(),
        };
      }

      // 成功メッセージ
      console.log('結果受取確認が完了しました');
    } catch (error) {
      state.error = error as Error;
      throw error;
    } finally {
      state.isLoading = false;
    }
  };

  /**
   * 再検査フラグを更新
   */
  const updateReExaminationStatus = async (
    id: number,
    hasReExamination: boolean,
  ): Promise<void> => {
    state.isLoading = true;
    state.error = null;

    try {
      const response = await $api.healthCheck.updateReExamination(id, hasReExamination);
      // 状態を更新
      if (state.healthCheckData && state.healthCheckData.id === id) {
        state.healthCheckData = {
          ...state.healthCheckData,
          reExamination: response.reExamination ?? hasReExamination,
        };
      }
    } catch (error) {
      state.error = error as Error;
      throw error;
    } finally {
      state.isLoading = false;
    }
  };

  /**
   * 状態をリセット
   */
  const resetHealthCheckState = (): void => {
    state.healthCheckData = null;
    state.healthCheckCards = [];
    state.isLoading = false;
    state.error = null;
  };

  return {
    ...toRefs(state),
    fetchHealthCheckData,
    fetchHealthCheckCards,
    updateConsultedStatus,
    updateReportedStatus,
    updateReExaminationStatus,
    resetHealthCheckState,
  };
};
